import axios from 'axios';
import { authorizedAxios } from './authorizedAxios';
import { getAuthHeader } from './auth';
import { generateChecksum } from '../utils/crypto';
import { API_BASE_URL } from '../config/api';
import { apiService } from './api';

// A user-initiated cancel (AbortController) surfaces as an axios CanceledError, which passes
// isAxiosError() but has no response. It must propagate unchanged so callers can tell a cancel
// apart from a real failure — rewrapping it as a generic "Network error" breaks abort detection
// and makes the retry layer treat the cancel as a retryable network fault.
const isRequestCancel = (error: unknown): boolean => {
  const e = error as { code?: string; name?: string } | undefined;
  return axios.isCancel(error) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';
};

// The server's message as an Error that keeps the server's code (RECORDING_ROLE_REQUIRED,
// VISIT_NOT_ACTIVE), so callers can tell a final refusal from a transient failure.
export const serverError = (error: unknown, fallback: string): Error & { code?: string; status?: number } => {
  const response = (error as { response?: { status?: number; data?: { error?: string; code?: string } } }).response;
  const wrapped = new Error(response?.data?.error || fallback) as Error & { code?: string; status?: number };
  if (response?.data?.code) wrapped.code = response.data.code;
  if (response?.status) wrapped.status = response.status;
  return wrapped;
};

export interface CreateRecordingDto {
  appointmentId?: string;
  encounterId?: string;
  source?: 'appointment' | 'encounter' | 'instant';
  encryptionMetadata: {
    iv: string;
    authTag: string;
    algorithm: string;
    keyId: string;
  };
  recordingMetadata: {
    duration: number;
    size: number;
    mimeType: string;
    templateId?: string;
    deviceInfo?: {
      userAgent: string;
      platform: string;
    };
    // Audio quality metadata for Azure optimization
    audioQuality?: {
      quality: 'excellent' | 'good' | 'fair' | 'poor' | 'very-poor';
      maxAmplitude: number;
      avgAmplitude: number;
      rmsLevel: number;
      nonSilentPercentage: number;
      sampleRate: number;
      channels: number;
      recommendations: {
        needsAmplification: boolean;
        needsNoiseReduction: boolean;
        needsNormalization: boolean;
        transcriptionDifficulty: 'low' | 'medium' | 'high' | 'very-high';
      };
    };
  };
}

export interface Recording {
  id: string;
  appointmentId: string;
  encounterId?: string;
  status: string;
  uploadUrl?: string;
  expiresIn?: number;
  metadata: any;
  createdAt: string;
}

export interface RecordingStatus {
  status: 'uploaded' | 'processing' | 'transcribed' | 'failed';
  transcriptId?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface TranscriptCheckResult {
  transcriptExists: boolean;
  hasContent: boolean;
  transcriptionInProgress: boolean;
  transcriptId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TranscriptExistsError extends Error {
  code: 'TRANSCRIPT_EXISTS';
  transcriptId: string;
  transcriptCreatedAt: string;
  requiresConfirmation: boolean;
}

class RecordingService {
  private baseUrl = API_BASE_URL;

  /**
   * Check if a transcript already exists for an appointment
   */
  async checkTranscript(appointmentId: string): Promise<TranscriptCheckResult> {
    try {
      const response = await authorizedAxios.post(
        `${this.baseUrl}/v1/recordings/check-transcript`,
        { appointmentId },
        { 
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to check transcript');
      }

      return response.data.data;
    } catch (error) {
      console.error('Failed to check transcript:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error checking transcript');
      }
      throw error;
    }
  }

  /**
   * Create recording and get upload URL
   */
  async createRecording(data: CreateRecordingDto): Promise<{
    recording: Recording;
    uploadUrl: string;
  }> {
    try {
      const response = await apiService.post<{ success: boolean; data: { recording: any }; error?: string }>(
        `/v1/recordings`,
        data,
        { timeout: 30000 } // 30 second timeout
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create recording');
      }

      const recordingData = response.data.recording;
      
      return {
        recording: {
          id: recordingData.id,
          appointmentId: recordingData.appointmentId,
          status: recordingData.status,
          metadata: recordingData.metadata,
          createdAt: recordingData.createdAt
        },
        uploadUrl: recordingData.uploadUrl
      };
    } catch (error) {
      console.error('Failed to create recording:', error);
      if (axios.isAxiosError(error)) {
        // If 402 Payment Required (subscription issues)
        if (error.response?.status === 402) {
          const errorCode = error.response?.data?.code || 'SUBSCRIPTION_REQUIRED';
          throw new Error(`subscription_required:${errorCode}`);
        }
        throw serverError(error, 'Network error creating recording');
      }
      throw error;
    }
  }

  /**
   * Upload encrypted audio directly to backend for Azure Speech transcription
   */
  async uploadRecording(
    recordingId: string,
    encryptedAudio: ArrayBuffer,
    options?: {
      onUploadProgress?: (progressEvent: any) => void;
      audioQualityMetadata?: any; // Quality report from integrity analysis
      azureOptimizations?: any;   // Optimization parameters
      signal?: AbortSignal;       // Aborts the in-flight upload when triggered
    }
  ): Promise<void> {
    try {
      // Create form data with encrypted audio
      const formData = new FormData();
      const audioBlob = new Blob([encryptedAudio], { type: 'application/octet-stream' });
      formData.append('audio', audioBlob, 'encrypted-recording.bin');

      // Add quality metadata for Azure optimization
      if (options?.audioQualityMetadata) {
        formData.append('audioQuality', JSON.stringify(options.audioQualityMetadata));
        console.log('🎯 Audio quality metadata included:', options.audioQualityMetadata);
      }

      if (options?.azureOptimizations) {
        formData.append('azureOptimizations', JSON.stringify(options.azureOptimizations));
        console.log('⚙️ Azure optimization parameters:', options.azureOptimizations);
      }

      // Log detailed info before upload
      console.log('📤 Uploading to Azure Speech Service:', {
        recordingId,
        size: encryptedAudio.byteLength,
        sizeInKB: (encryptedAudio.byteLength / 1024).toFixed(2),
        timestamp: new Date().toISOString(),
        hasQualityData: !!options?.audioQualityMetadata,
        hasOptimizations: !!options?.azureOptimizations
      });

      // Upload directly to backend
      const response = await authorizedAxios.post(
        `${this.baseUrl}/v1/recordings/${recordingId}/upload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000, // 5 minutes ceiling on the upload POST; transcription runs server-side after 200
          signal: options?.signal,
          onUploadProgress: (progressEvent) => {
            if (options?.onUploadProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              options.onUploadProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                progress: progress / 100
              });
            }
          }
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to upload audio');
      }

      console.log('✅ Audio uploaded successfully, transcription started');

      // Log Azure-specific response data
      if (response.data.azureInfo) {
        console.log('🔍 Azure Speech Service Response:', response.data.azureInfo);
      }

      if (response.data.processingConfig) {
        console.log('⚙️ Applied transcription config:', response.data.processingConfig);
      }

    } catch (error) {
      console.error('Failed to upload recording:', error);
      if (isRequestCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        // If 402 Payment Required (subscription issues)
        if (error.response?.status === 402) {
          const errorCode = error.response?.data?.code || 'SUBSCRIPTION_REQUIRED';
          throw new Error(`subscription_required:${errorCode}`);
        }
        // A 409 for a cancelled or no-show visit, or for a visit that already has a recording, is a
        // final refusal; keep its code so it is not taken for "already uploaded" and re-uploaded.
        if (error.response?.data?.code === 'VISIT_NOT_ACTIVE' || error.response?.data?.code === 'VISIT_ALREADY_RECORDED') {
          throw serverError(error, 'Network error uploading audio');
        }
        // If 409 Conflict (already uploaded), suggest using reupload
        if (error.response?.status === 409) {
          throw new Error(
            error.response?.data?.message ||
            'Recording was already uploaded. Use reuploadRecording() to re-upload.'
          );
        }
        throw serverError(error, 'Network error uploading audio');
      }
      throw new Error('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  /**
   * Re-upload encrypted audio for a recording that was already uploaded
   * This explicitly allows re-uploading by clearing the previous upload state
   */
  async reuploadRecording(
    recordingId: string,
    encryptedAudio: ArrayBuffer,
    options?: {
      onUploadProgress?: (progressEvent: any) => void;
      audioQualityMetadata?: any;
      azureOptimizations?: any;
      signal?: AbortSignal;       // Aborts the in-flight reupload when triggered
    }
  ): Promise<void> {
    try {
      // Create form data with encrypted audio
      const formData = new FormData();
      const audioBlob = new Blob([encryptedAudio], { type: 'application/octet-stream' });
      formData.append('audio', audioBlob, 'encrypted-recording.bin');

      // Add quality metadata for Azure optimization
      if (options?.audioQualityMetadata) {
        formData.append('audioQuality', JSON.stringify(options.audioQualityMetadata));
        console.log('🎯 Audio quality metadata included:', options.audioQualityMetadata);
      }

      if (options?.azureOptimizations) {
        formData.append('azureOptimizations', JSON.stringify(options.azureOptimizations));
        console.log('⚙️ Azure optimization parameters:', options.azureOptimizations);
      }

      console.log('📤 Re-uploading to Azure Speech Service:', {
        recordingId,
        size: encryptedAudio.byteLength,
        sizeInKB: (encryptedAudio.byteLength / 1024).toFixed(2),
        timestamp: new Date().toISOString()
      });

      // Use reupload endpoint which resets the recording state
      const response = await authorizedAxios.post(
        `${this.baseUrl}/v1/recordings/${recordingId}/reupload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000,
          signal: options?.signal,
          onUploadProgress: (progressEvent) => {
            if (options?.onUploadProgress && progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              options.onUploadProgress({
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                progress: progress / 100
              });
            }
          }
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to re-upload audio');
      }

      console.log('✅ Audio re-uploaded successfully, transcription started');

      if (response.data.azureInfo) {
        console.log('🔍 Azure Speech Service Response:', response.data.azureInfo);
      }

    } catch (error) {
      console.error('Failed to re-upload recording:', error);
      if (isRequestCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        throw serverError(error, 'Network error re-uploading audio');
      }
      throw new Error('Re-upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  /**
   * Complete upload and trigger processing
   */
  async completeUpload(recordingId: string, checksum: string): Promise<void> {
    try {
      const response = await authorizedAxios.post(
        `${this.baseUrl}/v1/recordings/${recordingId}/complete`,
        { checksum },
        { 
          headers: getAuthHeader(),
          timeout: 30000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to complete upload');
      }
    } catch (error) {
      console.error('Failed to complete upload:', error);
      if (axios.isAxiosError(error)) {
        throw serverError(error, 'Network error completing upload');
      }
      throw error;
    }
  }

  /**
   * Get recording status
   */
  async getRecordingStatus(recordingId: string): Promise<RecordingStatus> {
    try {
      const response = await authorizedAxios.get(
        `${this.baseUrl}/v1/recordings/${recordingId}/status`,
        { 
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to get recording status');
      }

      // Enhanced logging for transcription debugging
      const statusData = response.data.data;
      
      if (statusData.status === 'transcribed') {
        console.log('✅ Transcription completed successfully');
        if (statusData.transcriptPreview) {
          console.log('📝 Transcript preview:', statusData.transcriptPreview.substring(0, 200) + '...');
          console.log('📊 Transcript stats:', {
            length: statusData.transcriptPreview.length,
            wordCount: statusData.transcriptPreview.split(' ').length
          });
        }
        if (statusData.confidence) {
          console.log('🎯 Average confidence:', statusData.confidence);
        }
        if (statusData.azureMetrics) {
          console.log('📈 Azure metrics:', statusData.azureMetrics);
        }
      } else if (statusData.status === 'failed') {
        console.error('❌ Transcription failed:', statusData.error);
        if (statusData.azureError) {
          console.error('🔍 Azure error details:', statusData.azureError);
        }
        if (statusData.partialResult) {
          console.warn('⚠️ Partial transcription available:', statusData.partialResult);
        }
      } else if (statusData.status === 'processing') {
        console.log('⏳ Transcription in progress...');
        if (statusData.progress) {
          console.log('📊 Progress:', statusData.progress);
        }
      }

      return statusData;
    } catch (error) {
      console.error('Failed to get recording status:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error getting status');
      }
      throw error;
    }
  }

  /**
   * Get recording details
   */
  async getRecording(recordingId: string): Promise<Recording> {
    try {
      const response = await authorizedAxios.get(
        `${this.baseUrl}/v1/recordings/${recordingId}`,
        {
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to get recording');
      }

      const recording = response.data.data.recording;
      return {
        id: recording.id,
        appointmentId: recording.appointmentId,
        status: recording.status.current,
        metadata: recording.metadata,
        createdAt: recording.createdAt
      };
    } catch (error) {
      console.error('Failed to get recording:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error getting recording');
      }
      throw error;
    }
  }

  /**
   * List recordings
   */
  async getRecordings(options: {
    page?: number;
    limit?: number;
    status?: string;
    appointmentId?: string;
    encounterId?: string;
  } = {}): Promise<{
    recordings: Recording[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.status) params.append('status', options.status);
      if (options.appointmentId) params.append('appointmentId', options.appointmentId);
      if (options.encounterId) params.append('encounterId', options.encounterId);

      const response = await authorizedAxios.get(
        `${this.baseUrl}/v1/recordings?${params.toString()}`,
        { 
          headers: getAuthHeader(),
          timeout: 15000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to get recordings');
      }

      return {
        recordings: response.data.data.recordings.map((r: any) => ({
          id: r.id,
          appointmentId: r.appointmentId,
          encounterId: r.encounterId,
          status: r.status,
          metadata: r.metadata,
          createdAt: r.createdAt
        })),
        pagination: response.data.data.pagination
      };
    } catch (error) {
      console.error('Failed to get recordings:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error getting recordings');
      }
      throw error;
    }
  }

  /**
   * Delete existing transcript for an appointment
   */
  async deleteTranscript(appointmentId: string): Promise<void> {
    try {
      const response = await authorizedAxios.delete(
        `${this.baseUrl}/v1/transcription/appointment/${appointmentId}`,
        { 
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete transcript');
      }
    } catch (error) {
      console.error('Failed to delete transcript:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error deleting transcript');
      }
      throw error;
    }
  }

  /**
   * Delete recording
   */
  async deleteRecording(recordingId: string): Promise<void> {
    try {
      const response = await authorizedAxios.delete(
        `${this.baseUrl}/v1/recordings/${recordingId}`,
        { 
          headers: getAuthHeader(),
          timeout: 10000
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete recording');
      }
    } catch (error) {
      console.error('Failed to delete recording:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Network error deleting recording');
      }
      throw error;
    }
  }

  /**
   * Poll recording status until completion
   */
  async pollRecordingStatus(
    recordingId: string,
    onStatusUpdate?: (status: RecordingStatus) => void,
    maxAttempts: number = 60,
    intervalMs: number = 5000
  ): Promise<RecordingStatus> {
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      try {
        const status = await this.getRecordingStatus(recordingId);
        
        if (onStatusUpdate) {
          onStatusUpdate(status);
        }
        
        // Return if processing is complete (success or failure)
        if (status.status === 'transcribed' || status.status === 'failed') {
          return status;
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
        
      } catch (error) {
        console.error('Error polling recording status:', error);
        attempts++;
        
        if (attempts >= maxAttempts) {
          throw new Error('Timeout waiting for recording status');
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    
    throw new Error('Timeout waiting for recording completion');
  }

  /**
   * PHASE 3 OPTIMIZATION: Smart polling with exponential backoff and caching
   */
  async pollRecordingStatusSmart(
    recordingId: string,
    audioDuration: number,
    onProgressUpdate?: (progress: {
      stage: string;
      progress: number;
      elapsedTime: number;
      estimatedTotalTime: number;
      processingSpeedRatio?: number;
    }) => void,
    maxDurationMinutes: number = 10,
    signal?: AbortSignal
  ): Promise<RecordingStatus> {
    const throwIfAborted = () => {
      if (signal?.aborted) {
        throw new DOMException('Recording status polling aborted', 'AbortError');
      }
    };
    throwIfAborted();

    const startTime = Date.now();
    const maxDuration = maxDurationMinutes * 60 * 1000;
    
    // PHASE 3: Estimate total time based on Phase 2 metrics
    // Average processing speed ratio from metrics: ~0.47
    const estimatedProcessingTimeMs = audioDuration * 1000 * 0.5; // Slightly conservative
    const estimatedTotalTime = Math.max(15000, estimatedProcessingTimeMs); // Minimum 15 seconds
    
    let attempts = 0;
    let lastStatus: RecordingStatus | null = null;
    let currentStage = 'securing';
    let statusCache: { status: RecordingStatus; timestamp: number } | null = null;
    
    // PHASE 3: Smart polling intervals with exponential backoff
    const getNextInterval = (elapsedMs: number, attempts: number): number => {
      if (elapsedMs < 30000) return 2000;     // First 30 seconds: 2s intervals
      if (elapsedMs < 60000) return 5000;     // Next 30 seconds: 5s intervals  
      return 10000;                          // After 1 minute: 10s intervals
    };
    
    const determineStage = (status: RecordingStatus, elapsedMs: number): string => {
      if (status.status === 'uploaded') return 'decrypting';
      if (status.status === 'processing') {
        // Use elapsed time to estimate processing stage
        const progressRatio = elapsedMs / estimatedTotalTime;
        if (progressRatio < 0.05) return 'decrypting';
        if (progressRatio < 0.10) return 'converting';
        if (progressRatio < 0.90) return 'transcribing';
        if (progressRatio < 0.95) return 'enhancing';
        return 'finalizing';
      }
      if (status.status === 'transcribed') return 'completed';
      if (status.status === 'failed') return 'error';
      return 'securing';
    };
    
    const calculateProgress = (stage: string, elapsedMs: number): number => {
      const progressRatio = Math.min(1, elapsedMs / estimatedTotalTime);
      
      switch (stage) {
        case 'securing': return Math.min(2, progressRatio * 100);
        case 'decrypting': return Math.min(5, 2 + (progressRatio * 3));
        case 'converting': return Math.min(10, 5 + (progressRatio * 5));
        case 'transcribing': return Math.min(90, 10 + (progressRatio * 80));
        case 'enhancing': return Math.min(95, 90 + (progressRatio * 5));
        case 'finalizing': return Math.min(100, 95 + (progressRatio * 5));
        case 'completed': return 100;
        default: return progressRatio * 100;
      }
    };
    
    while (Date.now() - startTime < maxDuration) {
      throwIfAborted();
      try {
        const elapsedMs = Date.now() - startTime;
        
        // PHASE 3: Use cache if recent (within 2 seconds)
        if (statusCache && Date.now() - statusCache.timestamp < 2000) {
          lastStatus = statusCache.status;
        } else {
          lastStatus = await this.getRecordingStatus(recordingId);
          statusCache = { status: lastStatus, timestamp: Date.now() };
        }
        
        currentStage = determineStage(lastStatus, elapsedMs);
        const progress = calculateProgress(currentStage, elapsedMs);
        
        // PHASE 3: Enhanced progress callback
        if (onProgressUpdate) {
          onProgressUpdate({
            stage: currentStage,
            progress,
            elapsedTime: elapsedMs / 1000,
            estimatedTotalTime: estimatedTotalTime / 1000,
            processingSpeedRatio: elapsedMs / (audioDuration * 1000) // Real-time ratio
          });
        }
        
        // Return if processing is complete
        if (lastStatus.status === 'transcribed' || lastStatus.status === 'failed') {
          return lastStatus;
        }
        
        // PHASE 3: Smart interval calculation
        const nextInterval = getNextInterval(elapsedMs, attempts);
        await new Promise(resolve => setTimeout(resolve, nextInterval));
        attempts++;
        
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError') {
          throw error;
        }
        console.error('Error in smart polling:', error);
        attempts++;

        // Progressive backoff on errors
        const errorInterval = Math.min(30000, 5000 * Math.pow(1.5, attempts));
        await new Promise(resolve => setTimeout(resolve, errorInterval));
        
        if (Date.now() - startTime >= maxDuration) {
          throw new Error('Timeout waiting for recording completion');
        }
      }
    }
    
    throw new Error('Timeout waiting for recording completion');
  }
}

export const recordingService = new RecordingService();